import {
  ActionIcon,
  Badge,
  Button,
  Card,
  ColorInput,
  CopyButton,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconLink,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import {
  useSubmissionLinks,
  useCreateSubmissionLink,
  useUpdateSubmissionLink,
  useDeleteSubmissionLink,
  type SubmissionLink,
} from "@/hooks/useJournal";

interface SubmissionLinksManagerProps {
  projectId: string;
}

export function SubmissionLinksManager({ projectId }: SubmissionLinksManagerProps) {
  const { data, isLoading, error } = useSubmissionLinks(projectId);
  const [createModalOpened, createModalHandlers] = useDisclosure(false);

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={100} radius="md" />
        <Skeleton height={100} radius="md" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper p="xl" withBorder>
        <Text c="red">Failed to load submission links</Text>
      </Paper>
    );
  }

  const links = data?.links || [];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Text fw={600} size="lg">
            Submission Links
          </Text>
          <Text size="sm" c="dimmed">
            Create named links for external contributors
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={createModalHandlers.open}>
          Create Link
        </Button>
      </Group>

      {links.length === 0 ? (
        <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={48} variant="light" color="blue">
              <IconLink size={24} />
            </ThemeIcon>
            <Text c="dimmed" ta="center">
              No submission links yet
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Create named links like "Floor Guy" or "Kitchen Designer" to receive submissions
              from external contributors. Each submission is attributed to the link.
            </Text>
            <Button leftSection={<IconPlus size={16} />} onClick={createModalHandlers.open}>
              Create Your First Link
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Stack gap="sm">
          {links.map((link) => (
            <LinkCard key={link.id} link={link} projectId={projectId} />
          ))}
        </Stack>
      )}

      {/* Create Modal */}
      <CreateLinkModal
        opened={createModalOpened}
        onClose={createModalHandlers.close}
        projectId={projectId}
      />
    </Stack>
  );
}

interface LinkCardProps {
  link: SubmissionLink;
  projectId: string;
}

function LinkCard({ link, projectId }: LinkCardProps) {
  const [editModalOpened, editModalHandlers] = useDisclosure(false);
  const deleteLink = useDeleteSubmissionLink(projectId);

  // Generate submission URL using current window origin (not API origin)
  const submissionUrl = `${window.location.origin}/j/${link.hash}`;

  const handleDelete = async () => {
    if (!confirm(`Delete "${link.name}"? This cannot be undone.`)) return;
    await deleteLink.mutateAsync(link.id);
    notifications.show({
      title: "Link deleted",
      message: `"${link.name}" has been deleted`,
      color: "red",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            {/* Color indicator */}
            <div
              style={{
                width: 8,
                height: 48,
                borderRadius: 4,
                backgroundColor: link.color || "var(--mantine-color-grape-6)",
              }}
            />

            {/* Link info */}
            <div style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Text fw={600} truncate>
                  {link.name}
                </Text>
                {!link.isActive && (
                  <Badge color="gray" variant="light" size="xs">
                    Disabled
                  </Badge>
                )}
                {link.expiresAt && new Date(link.expiresAt) < new Date() && (
                  <Badge color="red" variant="light" size="xs">
                    Expired
                  </Badge>
                )}
              </Group>

              {link.description && (
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {link.description}
                </Text>
              )}

              <Group gap="md" mt={4}>
                <Text size="xs" c="dimmed">
                  {link.submissionCount} submission{link.submissionCount !== 1 ? "s" : ""}
                </Text>
                {link.lastUsedAt && (
                  <Text size="xs" c="dimmed">
                    Last used: {formatDate(link.lastUsedAt)}
                  </Text>
                )}
              </Group>
            </div>
          </Group>

          {/* Actions */}
          <Group gap="xs" wrap="nowrap">
            <CopyButton value={submissionUrl} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied!" : "Copy link"}>
                  <ActionIcon
                    variant={copied ? "filled" : "light"}
                    color={copied ? "green" : "blue"}
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>

            <Tooltip label="Open link">
              <ActionIcon
                variant="light"
                component="a"
                href={submissionUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconExternalLink size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Edit">
              <ActionIcon variant="light" onClick={editModalHandlers.open}>
                <IconPencil size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Delete">
              <ActionIcon
                variant="light"
                color="red"
                onClick={handleDelete}
                loading={deleteLink.isPending}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {/* Edit Modal */}
      <EditLinkModal
        opened={editModalOpened}
        onClose={editModalHandlers.close}
        link={link}
        projectId={projectId}
      />
    </>
  );
}

interface CreateLinkModalProps {
  opened: boolean;
  onClose: () => void;
  projectId: string;
}

function CreateLinkModal({ opened, onClose, projectId }: CreateLinkModalProps) {
  const createLink = useCreateSubmissionLink(projectId);

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      color: "#be4bdb", // grape
      defaultCategory: "",
    },
    validate: {
      name: (value) => (value.length < 1 ? "Name is required" : null),
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await createLink.mutateAsync({
        name: values.name,
        description: values.description || undefined,
        color: values.color || undefined,
        defaultCategory: values.defaultCategory || undefined,
      });

      notifications.show({
        title: "Link created!",
        message: `Share this link with ${values.name}`,
        color: "green",
      });

      form.reset();
      onClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create link",
        color: "red",
      });
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Create Submission Link" size="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Link Name"
            placeholder="e.g., Floor Guy, Kitchen Designer"
            description="This helps you identify who submissions come from"
            {...form.getInputProps("name")}
            required
          />

          <Textarea
            label="Description"
            placeholder="Optional description shown on the submission form"
            {...form.getInputProps("description")}
          />

          <ColorInput
            label="Color"
            placeholder="Pick a color"
            description="Used to identify this link in the timeline"
            {...form.getInputProps("color")}
            swatches={[
              "#25262b",
              "#868e96",
              "#fa5252",
              "#e64980",
              "#be4bdb",
              "#7950f2",
              "#4c6ef5",
              "#228be6",
              "#15aabf",
              "#12b886",
              "#40c057",
              "#82c91e",
              "#fab005",
              "#fd7e14",
            ]}
          />

          <Select
            label="Default Category"
            placeholder="Select category"
            description="Pre-selected category for submissions via this link"
            clearable
            data={[
              { value: "checkpoint", label: "Checkpoint" },
              { value: "issue", label: "Issue" },
              { value: "delivery", label: "Delivery" },
              { value: "receipt", label: "Receipt" },
              { value: "update", label: "Update" },
              { value: "general", label: "General" },
            ]}
            {...form.getInputProps("defaultCategory")}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={createLink.isPending}>
              Create Link
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

interface EditLinkModalProps {
  opened: boolean;
  onClose: () => void;
  link: SubmissionLink;
  projectId: string;
}

function EditLinkModal({ opened, onClose, link, projectId }: EditLinkModalProps) {
  const updateLink = useUpdateSubmissionLink(projectId);

  const form = useForm({
    initialValues: {
      name: link.name,
      description: link.description || "",
      color: link.color || "#be4bdb",
      defaultCategory: link.defaultCategory || "",
      isActive: link.isActive,
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await updateLink.mutateAsync({
        linkId: link.id,
        data: {
          name: values.name,
          description: values.description || null,
          color: values.color || null,
          defaultCategory: values.defaultCategory || null,
          isActive: values.isActive,
        },
      });

      notifications.show({
        title: "Link updated",
        message: "Changes have been saved",
        color: "green",
      });

      onClose();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update link",
        color: "red",
      });
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title={`Edit "${link.name}"`} size="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput label="Link Name" {...form.getInputProps("name")} required />

          <Textarea label="Description" {...form.getInputProps("description")} />

          <ColorInput
            label="Color"
            swatches={[
              "#25262b",
              "#868e96",
              "#fa5252",
              "#e64980",
              "#be4bdb",
              "#7950f2",
              "#4c6ef5",
              "#228be6",
              "#15aabf",
              "#12b886",
              "#40c057",
              "#82c91e",
              "#fab005",
              "#fd7e14",
            ]}
            {...form.getInputProps("color")}
          />

          <Select
            label="Default Category"
            clearable
            data={[
              { value: "checkpoint", label: "Checkpoint" },
              { value: "issue", label: "Issue" },
              { value: "delivery", label: "Delivery" },
              { value: "receipt", label: "Receipt" },
              { value: "update", label: "Update" },
              { value: "general", label: "General" },
            ]}
            {...form.getInputProps("defaultCategory")}
          />

          <Switch
            label="Link is active"
            description="Disable to prevent new submissions"
            {...form.getInputProps("isActive", { type: "checkbox" })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={updateLink.isPending}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
